import React, { useState, useEffect, useRef } from 'react';
import $ from 'jquery';
// Make sure DataTables CSS is imported if not done globally
// import 'datatables.net-dt/css/jquery.dataTables.css';
import 'datatables.net-dt/js/dataTables.dataTables.js';
import { Icon } from '@iconify/react';
import api from '../../services/api'; // Adjust path as needed
import { Modal } from 'react-bootstrap'; // Import Modal for use within the content prop
import DeleteConfirmationModal from '../DeleteConfirmationModal'; // Adjust path as needed
import { toast } from 'react-toastify';

// --- Custom Sort Function ---
// Sorts items with status 'pending' first, then alphabetically by name.
const sortPendingFirst = (a, b) => {
    // Prioritize 'pending' status
    if (a.status === 'pending' && b.status !== 'pending') {
        return -1; // a comes first
    }
    if (a.status !== 'pending' && b.status === 'pending') {
        return 1; // b comes first
    }

    // If statuses are the same priority (both pending or both not pending), sort by name
    const nameA = a.name?.toLowerCase() || ''; // Handle potential missing names safely
    const nameB = b.name?.toLowerCase() || '';

    if (nameA < nameB) {
        return -1; // a comes first alphabetically
    }
    if (nameA > nameB) {
        return 1; // b comes first alphabetically
    }

    return 0; // Names are equal or sorting doesn't apply
};


// --- Main Component ---
const DeveloperSalesTable = () => {
    const [developerSaless, setDeveloperSaless] = useState([]);
    const [loading, setLoading] = useState(false);
    const [tableKey, setTableKey] = useState(0); // Key to force table remount
    const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);
    const [selectedDeveloperSales, setSelectedDeveloperSales] = useState(null);
    const [actionType, setActionType] = useState(""); // 'block' or 'unblock'

    // State for auto-approve confirmation
    const [showAutoApproveConfirmationModal, setShowAutoApproveConfirmationModal] = useState(false);
    const [autoApproveAction, setAutoApproveAction] = useState(""); // 'enable' or 'disable'

    // Ref for the table element
    const tableRef = useRef(null);
    const dataTableInstance = useRef(null);

    // --- Data Fetching ---
    const fetchDeveloperSales = (incrementKey = false) => {
        console.log("Fetching developer sales...");
        setLoading(true);
        
        // Increment tableKey to force a fresh table mount when refetching
        if (incrementKey) {
            setTableKey(prev => prev + 1);
        }
        
        api.get('/users?type=developerSales')
            .then((res) => {
                console.log("API Response:", res.data);
                if (res.data && res.data.response && Array.isArray(res.data.response.data)) {
                    // *** APPLY SORTING HERE before setting state ***
                    const fetchedData = res.data.response.data;
                    fetchedData.sort(sortPendingFirst); // Use the custom sort function
                    setDeveloperSaless(fetchedData); // Set state with sorted data
                } else {
                    console.error("API response format is not as expected:", res.data);
                    setDeveloperSaless([]);
                    toast.error("Failed to fetch data: Invalid format received.");
                }
            })
            .catch((e) => {
                console.error("Error fetching developer sales:", e);
                toast.error("Failed to fetch developer sales data.");
                setDeveloperSaless([]);
            })
            .finally(() => {
                console.log("Fetch attempt finished.");
                setLoading(false);
            });
    };

    // --- Effects ---

    // Initialize DataTable - only runs when tableKey changes (fresh mount)
    useEffect(() => {
        // Use a small delay to ensure DOM is ready after React renders
        const initDataTable = setTimeout(() => {
            if (!tableRef.current) return;
            if (loading) return;
            if (developerSaless.length === 0) return;

            try {
                // Initialize DataTable only if not already initialized
                if (!$.fn.DataTable.isDataTable(tableRef.current)) {
                    dataTableInstance.current = $(tableRef.current).DataTable({
                        pageLength: 10,
                        order: [], // This prevents DataTables from applying initial sorting
                        columnDefs: [
                            { targets: 8, orderable: false, searchable: false } // Target Actions column (index 8)
                        ]
                    });
                }
            } catch (error) {
                console.error("Error initializing DataTable:", error);
            }
        }, 150);

        // Cleanup - only destroy when component unmounts
        return () => {
            clearTimeout(initDataTable);
        };
    }, [tableKey, loading, developerSaless.length]); // Re-run when tableKey changes


    // Fetch data on initial mount
    useEffect(() => {
        fetchDeveloperSales();
    }, []);


    // --- Action Handlers (No changes needed in handlers for sorting) ---

    // Approve Developer Sales
    const handleApprove = async (developerSales) => {
        if (!developerSales || !developerSales.id) return;
        try {
            await api.put(`/users/${developerSales.id}/approve`);
            toast.success("Developer Sales Approved Successfully!");
            // Use a small delay before fetching to ensure state is stable
            setTimeout(() => {
                fetchDeveloperSales(true);
            }, 300);
        } catch (error) {
            toast.error("Failed to Approve Developer Sales.");
            console.error("Approve error:", error.response?.data || error.message);
        }
    };

    // Decline Developer Sales
    const handleDecline = async (developerSales) => {
        if (!developerSales || !developerSales.id) return;
        try {
            await api.put(`/users/${developerSales.id}/decline`);
            toast.success("Developer Sales Declined Successfully!");
            // Use a small delay before fetching to ensure state is stable
            setTimeout(() => {
                fetchDeveloperSales(true);
            }, 300);
        } catch (error) {
            toast.error("Failed to Decline Developer Sales.");
            console.error("Decline error:", error.response?.data || error.message);
        }
    };

    // Setup confirmation for Block/Unblock
    const confirmAction = (developerSales, type) => {
        if (!developerSales || !developerSales.id) return;
        setSelectedDeveloperSales(developerSales);
        setActionType(type);
        setShowDeleteConfirmationModal(true);
    };

    // Execute Block/Unblock after confirmation
    const handleBlockUnblock = async () => {
        if (!selectedDeveloperSales || !selectedDeveloperSales.id || !actionType) return;
        try {
            const endpoint = actionType === "block" ? 'block' : 'unblock';
            await api.put(`/users/${selectedDeveloperSales.id}/${endpoint}`);
            toast.success(`Developer Sales ${actionType === 'block' ? 'Blocked' : 'Unblocked'} Successfully!`);
            handleCloseDeleteModal(true);
        } catch (error) {
            toast.error(`Failed to ${actionType} Developer Sales.`);
            console.error(`${actionType} error:`, error.response?.data || error.message);
            handleCloseDeleteModal(false);
        }
    };

    // Close Block/Unblock confirmation modal
    const handleCloseDeleteModal = (shouldRefetch = false) => {
        setShowDeleteConfirmationModal(false);
        if (shouldRefetch) {
            // Use a delay before fetching to ensure state is stable
            setTimeout(() => {
                fetchDeveloperSales(true);
            }, 300);
        }
        setTimeout(() => {
            setSelectedDeveloperSales(null);
            setActionType("");
        }, 200);
    };


    // Setup confirmation for Auto-Approve Toggle
    const handleAutoApproveButton = (developerSales) => {
        if (!developerSales || !developerSales.id) return;
        setSelectedDeveloperSales(developerSales);
        const action = developerSales.autoApprove ? "disable" : "enable";
        setAutoApproveAction(action);
        setShowAutoApproveConfirmationModal(true);
    };

    // Execute Auto-Approve Toggle after confirmation
    const handleConfirmAutoApprove = async () => {
        if (!selectedDeveloperSales || !selectedDeveloperSales.id || !autoApproveAction) return;
        try {
            const newAutoApprove = autoApproveAction === "enable";
            const response = await api.put(`/users/${selectedDeveloperSales.id}/autoApprove`, { autoApprove: newAutoApprove });

            if (response.data && response.data.status === true) {
                toast.success(`Developer Sales auto-approve ${autoApproveAction}d successfully!`);
                handleCloseAutoApproveModal(true);
            } else {
                toast.error(response.data?.message || "Failed to update auto-approve status.");
                handleCloseAutoApproveModal(false);
            }
        } catch (error) {
            toast.error("Failed to update auto-approve.");
            console.error("Auto-approve update error:", error.response?.data || error.message);
            handleCloseAutoApproveModal(false);
        }
    };

    // Close Auto-Approve confirmation modal
    const handleCloseAutoApproveModal = (shouldRefetch = false) => {
        setShowAutoApproveConfirmationModal(false);
        if (shouldRefetch) {
            // Use a delay before fetching to ensure state is stable
            setTimeout(() => {
                fetchDeveloperSales(true);
            }, 300);
        }
        setTimeout(() => {
            setSelectedDeveloperSales(null);
            setAutoApproveAction("");
        }, 200);
    };


    // --- Render (No changes needed in JSX structure for sorting) ---
    return (
        <div className="card basic-data-table px-3 py-3">
            <div className="card-body">
                {/* === SCROLLING WRAPPER - Key forces remount when data changes === */}
                <div className="table-responsive" key={`table-wrapper-${tableKey}`}>
                    <table ref={tableRef} className="table bordered-table mb-0" id="dataTable" style={{ width: "100%" }}>
                        {/* Table Head */}
                        <thead>
                            <tr>
                                <th scope="col">Developer</th>
                                <th scope="col">Name</th>
                                <th scope="col">Email</th>
                                <th scope="col">Mobile</th>
                                <th scope="col">Business Card</th>
                                <th scope="col">Total Projects</th>
                                <th scope="col">Total Sales</th>
                                <th scope="col">Created At</th>
                                <th scope="col">Actions</th>
                            </tr>
                        </thead>
                        {/* Table Body - Renders the already sorted 'developerSaless' state */}
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="9" className="text-center p-4">
                                        <div className="d-flex justify-content-center align-items-center" style={{minHeight: "200px"}}>
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            ) : developerSaless.length > 0 ? (
                                developerSaless.map((developerSales) => (
                                    <tr key={developerSales.id}>
                                        <td>{developerSales.developer?.name ?? 'N/A'}</td>
                                        <td>{developerSales.name ?? 'N/A'}</td>
                                        <td>{developerSales.email ?? 'N/A'}</td>
                                        <td>{developerSales.mobile ?? 'N/A'}</td>
                                        <td>
                                            {developerSales.businessCard ? (
                                                <a href={developerSales.businessCard} target="_blank" rel="noopener noreferrer" title="View Business Card">
                                                    <img
                                                        src={developerSales.businessCard}
                                                        alt="Business Card Preview"
                                                        style={{ maxWidth: '100px', maxHeight: '50px', cursor: 'pointer', objectFit: 'contain', border: '1px solid #ccc' }}
                                                        onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; e.target.alt = 'Image unavailable'; }}
                                                    />
                                                </a>
                                            ) : 'No Card'}
                                        </td>
                                        <td>{Number(developerSales.project_count) || 0}</td>
                                        <td>{Number(developerSales.total_sales) || 0}</td>
                                        <td>{developerSales.created_at ? new Date(developerSales.updated_at).toLocaleDateString('en-GB') : 'N/A'}</td>

                                        <td className="d-flex gap-2 flex-wrap justify-content-start">
                                            {/* Approve & Decline Buttons */}
                                            {developerSales.status === "pending" && (
                                                <>
                                                    <button onClick={() => handleApprove(developerSales)} className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center border-0 p-0" title="Approve" aria-label={`Approve ${developerSales.name}`}>
                                                        <Icon icon="mdi:check-circle" width="20" height="20" />
                                                    </button>
                                                    <button onClick={() => handleDecline(developerSales)} className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center border-0 p-0" title="Reject" aria-label={`Reject ${developerSales.name}`}>
                                                        <Icon icon="mdi:cancel" width="20" height="20" />
                                                    </button>
                                                </>
                                            )}
                                            {/* Block / Unblock Button */}
                                            {developerSales.status !== "pending" && (
                                                <button onClick={() => confirmAction(developerSales, developerSales.isBlocked === 0 ? 'block' : 'unblock')} className={`w-32-px h-32-px rounded-circle d-inline-flex align-items-center justify-content-center border-0 p-0 ${developerSales.isBlocked === 0 ? "bg-danger-focus text-danger-main" : "bg-success-focus text-success-main"}`} title={developerSales.isBlocked === 0 ? "Block" : "Unblock"} aria-label={developerSales.isBlocked === 0 ? `Block ${developerSales.name}` : `Unblock ${developerSales.name}`}>
                                                    <Icon icon={developerSales.isBlocked === 0 ? "mdi:account-cancel-outline" : "mdi:account-lock-open-outline"} width="20" height="20" />
                                                </button>
                                            )}
                                            {/* Auto-Approve Toggle Button */}
                                            {developerSales.status === "approved" && (
                                                <button onClick={() => handleAutoApproveButton(developerSales)} className={`w-32-px h-32-px rounded-circle d-inline-flex align-items-center justify-content-center border-0 p-0 ${developerSales.autoApprove ? "bg-success-focus text-success-main" : "bg-warning-focus text-warning-main"}`} title={developerSales.autoApprove ? "Disable Auto-Approve" : "Enable Auto-Approve"} aria-label={developerSales.autoApprove ? `Disable auto-approve for ${developerSales.name}` : `Enable auto-approve for ${developerSales.name}`}>
                                                    <Icon icon={developerSales.autoApprove ? "mdi:toggle-switch" : "mdi:toggle-switch-off"} width="24" height="24" />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="9" className="text-center p-4">
                                        No developer sales data found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div> {/* === END SCROLLING WRAPPER === */}
            </div>

            {/* --- Modals (No changes needed) --- */}
            {/* Confirmation Modal for Block/Unblock */}
            {showDeleteConfirmationModal && selectedDeveloperSales && (
                <DeleteConfirmationModal
                    show={showDeleteConfirmationModal}
                    handleDelete={handleBlockUnblock}
                    handleClose={() => handleCloseDeleteModal(false)}
                   
                    content={
                        <>
                            <Modal.Header closeButton>
                                <Modal.Title>{actionType === 'block' ? 'Block Developer Sales' : 'Unblock Developer Sales'}</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <p>Are you sure you want to {actionType} this Developer Sales account for "{selectedDeveloperSales.name}"?</p>
                            </Modal.Body>
                        </>
                    }
                />
            )}
            {/* Confirmation Modal for Auto-Approve Toggle */}
            {showAutoApproveConfirmationModal && selectedDeveloperSales && (
                <DeleteConfirmationModal
                    show={showAutoApproveConfirmationModal}
                    handleDelete={handleConfirmAutoApprove}
                    handleClose={() => handleCloseAutoApproveModal(false)}
                    content={
                        <>
                            <Modal.Header closeButton>
                                <Modal.Title>{autoApproveAction === 'enable' ? 'Enable Auto-Approve' : 'Disable Auto-Approve'}</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <p>Are you sure you want to {autoApproveAction} auto-approve for "{selectedDeveloperSales.name}"?</p>
                            </Modal.Body>
                        </>
                    }
                />
            )}
        </div>
    );
};

export default DeveloperSalesTable;